import useAuth from '@app/auth/use-auth';
import { BulkActionBar } from '@app/components/data-table/bulk-edit/bulk-action-bar';
import { makeSelectColumn } from '@app/components/data-table/bulk-edit/select-column';
import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import { makeUpdateItemsAction } from '@app/components/item-edit/update-items-modal-body';
import { toPickerPeriods } from '@app/components/multi-period-picker';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import {
  FoodFilterColumn,
  foodFilterKeys,
  FoodFilterOption,
  foodFilters,
} from '@app/sold-goods/filters';
import { soldGoodsRoutes } from '@app/sold-goods/sold-goods-routes';
import {
  FoodOperationActivitiesList,
  OperationReportTypeEnum,
  PaginatedFoodOperationActivitiesListList,
} from '@shared/api/types';
import { useSoldFoodAndDrinkTableColumns } from './sold-food-and-drink-table-columns';
import SoldFoodAndDrinkTableToolbar from './sold-food-and-drink-table-toolbar';

interface SoldFoodAndDrinkTableProps {
  data?: PaginatedFoodOperationActivitiesListList;
  status?: string;
  isLoading?: boolean;
  isFetching?: boolean;
  isPreviousData?: boolean;
  isFetchedAfterMount?: boolean;
}

export default function SoldFoodAndDrinkTable({
  data,
  status,
  isLoading,
  isPreviousData,
  isFetchedAfterMount,
}: SoldFoodAndDrinkTableProps) {
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const navigate = useNavigate();
  const { user } = useAuth();

  const { count } = data || {};
  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const filterOptions = useServerFilterOptions(
    foodFilterKeys,
    data?.filters || {}
  );

  const useFilterOptions = useMemo(() => {
    const opts: FilterOptions<FoodFilterOption[]> = {} as FilterOptions<
      FoodFilterOption[]
    >;
    foodFilterKeys.forEach((key) => {
      opts[key] = filterOptions[`${key}Filter`];
    });
    return opts;
  }, [filterOptions]);

  const { table, context } = useServerDataTable({
    data: data?.results || [],
    columns: [
      // Selection checkboxes are staff-only (bulk actions are gated to staff).
      ...(user?.isStaff
        ? [makeSelectColumn<FoodOperationActivitiesList>()]
        : []),
      ...useSoldFoodAndDrinkTableColumns({
        filters: useFilterOptions,
        timePeriods: toPickerPeriods(data?.filters?.timePeriods),
      }),
    ],
    pageCount,
    shallow: false,
    getRowId: (row) => row.uuid,
    initialState: {
      columnVisibility: {
        uuid: false,
        [FoodFilterColumn.Period]: false,
        [FoodFilterColumn.Site]: false,
        [FoodFilterColumn.Customer]: false,
        [FoodFilterColumn.ActivitiesImport]: false,
        [FoodFilterColumn.Tag]: false,
        [FoodFilterColumn.RemoteId]: false,
      },
      columnOrder: ['select', 'remoteId', 'food'],
    },
  });

  // Main Food and drink table: one unified "Update items" action, mirroring
  // the purchased table. Rows are keyed by activity uuid; the "Food" target
  // re-models the food activities while the other targets change-type the
  // SoldGoods.
  const bulkActions = useMemo(
    () => [
      makeUpdateItemsAction({
        currentType: 'food',
        reportType: OperationReportTypeEnum.sales,
        resolveUuids: (table) =>
          table
            .getFilteredSelectedRowModel()
            .rows.map(
              (row) =>
                (row.original as FoodOperationActivitiesList).soldGoodInfo.uuid
            ),
        resolveFoodActivityUuids: (table) =>
          table
            .getFilteredSelectedRowModel()
            .rows.map(
              (row) => (row.original as FoodOperationActivitiesList).uuid
            ),
      }),
    ],
    []
  );

  return (
    <>
      <DataTable
        table={table}
        context={context}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
        tableLoading={!data && status === 'loading'}
        className="bg-gray-00 text-left"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) =>
          navigate(
            generatePath(soldGoodsRoutes.SOLD_FOOD_AND_DRINK_ITEM, {
              item_uuid: row?.original.uuid,
            })
          )
        }
        rowClickColumnDisabledIds={(row) => [
          ...(row.original.soldGoodInfo.productUuid! ||
          row.original.soldGoodInfo.purchasedGoodUuid!
            ? ['matchedFood']
            : []),
        ]}
      >
        <DataTableToolbar
          table={table}
          filterOrder={[
            'food',
            ...Object.values(foodFilters).map((f) => f.column),
          ]}
        >
          <SoldFoodAndDrinkTableToolbar />
        </DataTableToolbar>
      </DataTable>
      <BulkActionBar table={table} actions={bulkActions} />
    </>
  );
}
